<template>
  <div class="w-full max-w-full min-w-0 overflow-x-hidden" dir="rtl">
    <div v-if="isAuthorized">
      <!-- Header -->
      <div class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden mb-4 w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-4 bg-gray-900/50 border-b border-gray-700 min-w-0">
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
            <div class="min-w-0 overflow-hidden">
              <button @click="goBack" class="text-[11px] text-gray-400 hover:text-gray-200 transition mb-2">
                <i class="fas fa-arrow-right ml-1"></i> بازگشت به خلاصه محصولات
              </button>
              <div class="flex items-center gap-2 min-w-0">
                <div
                    v-if="detail"
                    class="shrink-0 w-10 h-10 aspect-square rounded-lg overflow-hidden border border-gray-700 bg-gray-800 flex items-center justify-center"
                    :class="imgOk(detail.image) ? 'cursor-zoom-in' : ''"
                    @click="zoomImage(detail.image)"
                >
                  <img v-if="imgOk(detail.image)" :src="img_source(detail.image)" class="w-full h-full object-cover" @error="markBroken(detail.image)">
                  <i v-else class="fas fa-image text-gray-600 text-sm"></i>
                </div>
                <h3 class="text-sm font-bold text-gray-100 truncate">{{ detail ? detail.product_name : 'گزارش محصول' }}</h3>
              </div>
              <p v-if="detail && detail.note" class="text-[10px] text-amber-300/80 mt-1">{{ detail.note }}</p>
            </div>

            <button
                @click="fetchDetail"
                :disabled="loading"
                class="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-200 hover:bg-gray-700 transition disabled:opacity-50 shrink-0"
            >
              <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
              بروزرسانی
            </button>
          </div>
        </header>

        <!-- Date range filter -->
        <section class="px-3 sm:px-4 py-4 bg-gray-900/30 border-b border-gray-700 min-w-0 overflow-hidden">
          <div class="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 min-w-0">
            <div class="col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">از تاریخ</label>
              <date placeholder="از تاریخ" v-model="fromDate" />
            </div>

            <div class="col-span-1 min-w-0">
              <label class="block text-[10px] sm:text-[11px] text-gray-400 mb-1.5 truncate">تا تاریخ</label>
              <date placeholder="تا تاریخ" v-model="toDate" />
            </div>

            <div class="col-span-2 flex items-end gap-2 min-w-0">
              <button @click="applyFilter" class="px-3 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-xs text-white transition shrink-0">
                اعمال بازه
              </button>
              <button v-if="fromDate || toDate" @click="showAllHistory" class="px-3 py-2 rounded-xl bg-gray-800 border border-gray-600 text-xs text-gray-300 hover:bg-gray-700 transition shrink-0">
                کل تاریخچه
              </button>
            </div>
          </div>
        </section>

        <!-- Summary cards -->
        <section v-if="detail" class="px-3 sm:px-4 py-4 grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 min-w-0">
          <div class="rounded-xl bg-blue-600/10 border border-blue-500/20 px-3 py-2 min-w-0 overflow-hidden">
            <p class="text-[10px] text-blue-300/80 truncate">آنلاین ({{ formatNumber(detail.online_quantity) }} عدد)</p>
            <p class="text-xs font-bold text-blue-300 mt-1 truncate" dir="ltr">{{ formatMoney(detail.online_revenue) }}</p>
            <p class="text-[10px] text-blue-300/60 mt-1 truncate">سود: {{ formatMoney(detail.online_profit) }}</p>
          </div>
          <div class="rounded-xl bg-purple-600/10 border border-purple-500/20 px-3 py-2 min-w-0 overflow-hidden">
            <p class="text-[10px] text-purple-300/80 truncate">حضوری ({{ formatNumber(detail.in_store_quantity) }} عدد)</p>
            <p class="text-xs font-bold text-purple-300 mt-1 truncate" dir="ltr">{{ formatMoney(detail.in_store_revenue) }}</p>
            <p class="text-[10px] text-purple-300/60 mt-1 truncate">سود: {{ formatMoney(detail.in_store_profit) }}</p>
          </div>
          <div class="rounded-xl bg-emerald-600/10 border border-emerald-500/20 px-3 py-2 min-w-0 overflow-hidden">
            <p class="text-[10px] text-emerald-300/80 truncate">جمع کل ({{ formatNumber(detail.total_quantity) }} عدد)</p>
            <p class="text-xs font-bold text-emerald-400 mt-1 truncate" dir="ltr">{{ formatMoney(detail.total_revenue) }}</p>
            <p class="text-[10px] text-emerald-300/60 mt-1 truncate">سود کل: {{ formatMoney(detail.total_profit) }}</p>
          </div>
          <div class="rounded-xl bg-gray-900/70 border border-gray-700 px-3 py-2 min-w-0 overflow-hidden">
            <p class="text-[10px] text-gray-500 truncate">موجودی فعلی</p>
            <p class="text-xs font-bold text-gray-200 mt-1 truncate" dir="ltr">{{ formatNumber(detail.inventory_count) }}</p>
          </div>
        </section>

        <!-- Charts -->
        <section v-if="detail && detail.daily_groups && detail.daily_groups.length > 1" class="px-3 sm:px-4 pb-4 min-w-0">
          <div class="bg-gray-900/50 border border-gray-700 rounded-xl p-3 min-w-0">
            <h4 class="text-[11px] font-bold text-gray-300 mb-2">روند فروش و سود در بازه</h4>
            <div class="relative h-64"><canvas ref="trendChart"></canvas></div>
          </div>
        </section>
      </div>

      <!-- Loading / error -->
      <div v-if="loading" class="p-10 text-center">
        <i class="fas fa-circle-notch fa-spin text-indigo-400 text-2xl"></i>
        <p class="text-gray-400 mt-3 text-sm">در حال دریافت اطلاعات...</p>
      </div>

      <div v-else-if="error" class="p-10 text-center">
        <i class="fas fa-exclamation-triangle text-red-400 text-2xl"></i>
        <p class="text-red-400 mt-3 text-sm">{{ error }}</p>
        <button @click="fetchDetail" class="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-xl text-white text-xs transition">
          تلاش مجدد
        </button>
      </div>

      <!-- Transactions -->
      <div v-else-if="detail" class="bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden w-full max-w-full min-w-0">
        <header class="px-3 sm:px-4 py-3 bg-gray-900/50 border-b border-gray-700">
          <h4 class="text-xs font-bold text-gray-200">تاریخچه‌ی تراکنش‌ها ({{ formatNumber(count) }})</h4>
        </header>

        <!-- Mobile cards -->
        <div class="md:hidden p-3 space-y-3 w-full max-w-full min-w-0 overflow-hidden">
          <article
              v-for="(tx, idx) in detail.transactions"
              :key="idx"
              class="rounded-2xl p-3 shadow-sm w-full max-w-full min-w-0 overflow-hidden border"
              :class="isAdminMode && tx.cost_is_estimated ? 'bg-amber-500/5 border-amber-500/30' : 'bg-gray-900/45 border-gray-700/80'"
          >
            <div class="flex items-center justify-between gap-2 min-w-0">
              <div class="flex items-center gap-1.5 flex-wrap">
                <span :class="sourceBadgeClass(tx.source)" class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-medium border">
                  <i :class="sourceIcon(tx.source)"></i>
                  {{ sourceLabel(tx.source) }}
                </span>
                <span v-if="tx.discount_amount > 0" class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-medium border bg-rose-600/15 text-rose-300 border-rose-500/30">
                  <i class="fas fa-tag"></i> {{ formatMoney(tx.discount_amount) }}
                </span>
              </div>
              <span class="text-[10px] text-gray-500 truncate">{{ tx.date | toJalali('jYYYY/jMM/jDD HH:mm') }}</span>
            </div>

            <div class="grid grid-cols-2 gap-2 mt-3 min-w-0 text-[11px]">
              <div class="min-w-0 overflow-hidden">
                <p class="text-gray-500 truncate">تعداد</p>
                <p class="text-gray-300 mt-0.5 truncate" dir="ltr">{{ formatNumber(tx.quantity) }}</p>
              </div>
              <div class="min-w-0 overflow-hidden">
                <p class="text-gray-500 truncate">مبلغ کل</p>
                <p class="text-gray-200 font-bold mt-0.5 truncate" dir="ltr">{{ formatMoney(tx.line_total) }}</p>
              </div>
              <div class="min-w-0 overflow-hidden">
                <p class="text-gray-500 truncate">بهای هر عدد</p>
                <p class="text-gray-400 mt-0.5 truncate" dir="ltr">
                  {{ formatMoney(tx.unit_cost) }}
                  <i v-if="isAdminMode && tx.cost_is_estimated" class="fas fa-info-circle text-amber-400 mr-1" title="تخمینی"></i>
                </p>
              </div>
              <div class="min-w-0 overflow-hidden">
                <p class="text-gray-500 truncate">سود ردیف</p>
                <p class="font-bold mt-0.5 truncate" dir="ltr" :class="profitClass(tx)">
                  {{ profitLabel(tx) }}
                  <i v-if="isAdminMode && tx.cost_is_estimated" class="fas fa-info-circle text-[10px]" title="تخمینی"></i>
                </p>
              </div>
            </div>

            <p class="text-[10px] text-gray-500 mt-3 pt-3 border-t border-gray-700/70 truncate" dir="ltr">{{ tx.order_reference }}</p>
          </article>

          <div v-if="detail.transactions.length === 0" class="px-4 py-12 text-center rounded-2xl bg-gray-900/40 border border-gray-700">
            <i class="fas fa-inbox text-3xl text-gray-600 mb-3 block"></i>
            <p class="text-gray-400 text-sm">تراکنشی در این بازه ثبت نشده</p>
          </div>
        </div>

        <!-- Desktop table -->
        <div class="hidden md:block w-full max-w-full min-w-0 overflow-x-auto table-scroll-safe">
          <table class="w-full min-w-[980px] text-sm">
            <thead class="bg-gray-900/70 border-b border-gray-700">
            <tr>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">کانال</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">تاریخ</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">سفارش</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">تعداد</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">قیمت هر عدد</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">مبلغ کل</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">بهای هر عدد</th>
              <th class="px-4 py-3 text-right text-xs font-medium text-gray-400">سود ردیف</th>
            </tr>
            </thead>

            <tbody class="divide-y divide-gray-700/50">
            <tr v-for="(tx, idx) in detail.transactions" :key="idx" class="hover:bg-gray-700/30 transition" :class="isAdminMode && tx.cost_is_estimated ? 'bg-amber-500/5' : ''">
              <td class="px-4 py-3 text-xs">
                <div class="flex items-center gap-1.5 flex-wrap">
                  <span :class="sourceBadgeClass(tx.source)" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border">
                    <i :class="sourceIcon(tx.source)"></i>
                    {{ sourceLabel(tx.source) }}
                  </span>
                  <span v-if="tx.discount_amount > 0" :title="`تخفیف دستی: ${formatMoney(tx.discount_amount)}`" class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium border bg-rose-600/15 text-rose-300 border-rose-500/30">
                    <i class="fas fa-tag"></i> {{ formatMoney(tx.discount_amount) }}
                  </span>
                </div>
              </td>
              <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{{ tx.date | toJalali('jYYYY/jMM/jDD HH:mm') }}</td>
              <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap text-right" dir="ltr">{{ tx.order_reference }}</td>
              <td class="px-4 py-3 text-xs text-gray-300 whitespace-nowrap text-right" dir="ltr">{{ formatNumber(tx.quantity) }}</td>
              <td class="px-4 py-3 text-xs text-gray-300 whitespace-nowrap text-right" dir="ltr">{{ formatMoney(tx.unit_price) }}</td>
              <td class="px-4 py-3 text-xs text-gray-200 font-bold whitespace-nowrap text-right" dir="ltr">{{ formatMoney(tx.line_total) }}</td>
              <td class="px-4 py-3 text-xs text-gray-400 whitespace-nowrap text-right" dir="ltr">
                {{ formatMoney(tx.unit_cost) }}
                <i v-if="isAdminMode && tx.cost_is_estimated" class="fas fa-info-circle text-amber-400 mr-1" title="بهای تخمینی، نه اسنپ‌شات لحظه‌ی فروش"></i>
              </td>
              <td class="px-4 py-3 text-xs font-bold whitespace-nowrap text-right" dir="ltr" :class="profitClass(tx)">
                {{ profitLabel(tx) }}
                <i v-if="isAdminMode && tx.cost_is_estimated" class="fas fa-info-circle text-[10px]" title="تخمینی"></i>
              </td>
            </tr>

            <tr v-if="detail.transactions.length === 0">
              <td colspan="8" class="px-4 py-12 text-center">
                <i class="fas fa-inbox text-3xl text-gray-600 mb-3 block"></i>
                <p class="text-gray-400 text-sm">تراکنشی در این بازه ثبت نشده</p>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <footer v-if="count > 0" class="px-3 sm:px-4 py-4 bg-gray-900/30 border-t border-gray-700 min-w-0 overflow-hidden">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 min-w-0">
            <div class="text-[11px] sm:text-xs text-gray-400 text-center sm:text-right min-w-0">
              نمایش
              <b class="text-gray-200">{{ formatNumber(fromItem) }}</b>
              تا
              <b class="text-gray-200">{{ formatNumber(toItem) }}</b>
              از
              <b class="text-gray-200">{{ formatNumber(count) }}</b>
              تراکنش
            </div>

            <div class="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-2 min-w-0">
              <button
                  @click="goToPage(page - 1)"
                  :disabled="page === 1 || loading"
                  class="px-3 py-2 rounded-xl border text-xs transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :class="page > 1 ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700' : 'bg-gray-900 border-gray-700 text-gray-500'"
              >
                قبلی
              </button>

              <span class="px-3 py-2 rounded-xl bg-gray-800 border border-gray-700 text-xs text-gray-300 text-center truncate">
                {{ formatNumber(page) }} / {{ formatNumber(totalPages) }}
              </span>

              <button
                  @click="goToPage(page + 1)"
                  :disabled="page >= totalPages || loading"
                  class="px-3 py-2 rounded-xl border text-xs transition disabled:opacity-50 disabled:cursor-not-allowed"
                  :class="page < totalPages ? 'bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700' : 'bg-gray-900 border-gray-700 text-gray-500'"
              >
                بعدی
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <div v-else class="w-full rounded-2xl border border-red-500/30 bg-red-950/40 px-4 py-5 text-right">
      <h3 class="text-sm font-bold text-red-100">دسترسی غیرمجاز</h3>
      <p class="mt-2 text-xs text-red-200">دسترسی برای شما مجاز نیست.</p>
    </div>

    <!-- Image zoom overlay -->
    <div
        v-if="zoomedImage"
        @click="zoomedImage = null"
        class="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
    >
      <img :src="zoomedImage" class="max-w-full max-h-full rounded-2xl shadow-2xl object-contain" @click.stop>
      <button
          @click="zoomedImage = null"
          class="absolute top-4 left-4 sm:top-6 sm:left-6 w-9 h-9 rounded-full bg-gray-900/80 border border-gray-600 text-gray-200 hover:bg-gray-800 transition flex items-center justify-center text-lg"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script>
import getPartnerCalculationsApi from './getPartnerCalculationsApi'
import date from "@/components/mcomponents/cleave/Date"
import moment from "moment-jalaali"

export default {
  name: 'PartnerCalculationsProductDetail',

  mixins: [getPartnerCalculationsApi],

  components: { date },

  props: {
    partnerId: {
      type: [String, Number],
      default: null,
    },
    productIdProp: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      error: null,

      fromDate: '',
      toDate: '',

      detail: null,

      page: 1,
      pageSize: 30,
      count: 0,

      trendChart: null,

      zoomedImage: null,
      brokenImages: {},
    }
  },

  computed: {
    isAdminMode() {
      return !!this.partnerId
    },
    isAuthorized() {
      if (this.isAdminMode) return this.user.user_type === 'ma' || this.user.is_superuser
      return this.user.user_type === 'prtnr' || this.user.user_type === 'ma'
    },
    productId() {
      return this.isAdminMode ? this.productIdProp : this.$route.params.id
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.count / this.pageSize))
    },
    fromItem() {
      if (this.count === 0) return 0
      return (this.page - 1) * this.pageSize + 1
    },
    toItem() {
      return Math.min(this.page * this.pageSize, this.count)
    },
  },

  watch: {
    // اگر کاربر از یک صفحه‌ی جزئیات مستقیم به محصول دیگری navigate کرد (بدون رفتن به لیست)
    productId() {
      this.fromDate = ''
      this.toDate = ''
      this.page = 1
      this.fetchDetail()
    },
  },

  methods: {
    goBack() {
      if (this.isAdminMode) {
        this.$emit('back')
        return
      }
      this.$router.push({ name: 'PartnerCalculations' })
    },

    getParams() {
      const params = { page: this.page, page_size: this.pageSize }
      if (this.fromDate) params.from = this.fromDate
      if (this.toDate) params.to = this.toDate
      return params
    },

    fetchDetail() {
      this.loading = true
      this.error = null

      const onSuccess = data => {
        this.detail = data
        this.count = data.count || 0
        // اگر کاربر خودش بازه‌ای انتخاب نکرده بود، فیلترها خالی می‌مونن — یعنی الان کل
        // تاریخچه دیده می‌شه، نه یک بازه‌ی پیش‌فرض
        if (!this.fromDate && data.date_from) this.fromDate = String(data.date_from).slice(0, 19).replace('T', ' ')
        if (!this.toDate && data.date_to) this.toDate = String(data.date_to).slice(0, 19).replace('T', ' ')
        this.loading = false
        this.$nextTick(() => this.renderTrendChart())
      }
      const onError = () => {
        this.error = 'خطا در دریافت اطلاعات این محصول'
        this.loading = false
      }

      if (this.isAdminMode) {
        this.getAdminPartnerCalculationDetail(this.partnerId, this.productId, this.getParams(), onSuccess, onError)
      } else {
        this.getPartnerCalculationDetail(this.productId, this.getParams(), onSuccess, onError)
      }
    },

    showAllHistory() {
      this.fromDate = ''
      this.toDate = ''
      this.page = 1
      this.fetchDetail()
    },

    applyFilter() {
      this.page = 1
      this.fetchDetail()
    },

    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.page) return
      this.page = page
      this.fetchDetail()
    },

    sourceLabel(source) {
      if (source === 'online') return 'آنلاین'
      if (source === 'in_store') return 'حضوری'
      if (source === 'in_store_return') return 'مرجوعی حضوری'
      return 'نامشخص'
    },

    sourceIcon(source) {
      if (source === 'online') return 'fas fa-globe'
      if (source === 'in_store') return 'fas fa-store'
      if (source === 'in_store_return') return 'fas fa-undo-alt'
      return 'fas fa-question'
    },

    sourceBadgeClass(source) {
      if (source === 'online') return 'bg-blue-600/15 text-blue-300 border-blue-500/30'
      if (source === 'in_store') return 'bg-purple-600/15 text-purple-300 border-purple-500/30'
      if (source === 'in_store_return') return 'bg-rose-600/15 text-rose-300 border-rose-500/30'
      return 'bg-gray-600/15 text-gray-300 border-gray-500/30'
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '۰'
      const number = Number(value)
      if (Number.isNaN(number)) return '۰'
      return new Intl.NumberFormat('fa-IR').format(number)
    },

    formatMoney(value) {
      return `${this.formatNumber(Math.round(Number(value || 0)))} تومان`
    },

    // اگه عکس واقعاً لود نشه (فایل حذف‌شده/لینک خراب)، v-if صرفاً وجود URL رو چک می‌کنه
    // نه لود شدنش -- برای همین با @error این URL رو به‌عنوان خراب علامت می‌زنیم تا آیکون
    // جایگزین نشون داده بشه، نه یک باکس خالی.
    imgOk(url) {
      return !!url && !this.brokenImages[url]
    },

    markBroken(url) {
      this.$set(this.brokenImages, url, true)
    },

    zoomImage(url) {
      if (this.imgOk(url)) this.zoomedImage = this.img_source(url)
    },

    // بهای ردیف‌های تخمینی (بدون اسنپ‌شاتِ نرخ ارز لحظه‌ی فروش) با نرخ *فعلی* حساب می‌شود؛
    // اگر نرخ ارز از اون موقع بالا رفته باشه، همین باعث می‌شه سود منفیِ نادرست نشون بده --
    // پس برای این ردیف‌ها به‌جای عدد منفیِ نامطمئن، «نامشخص» نشون می‌دیم. این پرچم فقط
    // برای مدیر کل نمایش داده می‌شه، طبق همون قاعده‌ای که «بهای تخمینی» رو از پنل خود
    // شریک مخفی نگه می‌داره.
    profitLabel(tx) {
      const estimated = this.isAdminMode && tx.cost_is_estimated
      if (estimated && Number(tx.line_profit) < 0) return 'نامشخص'
      return this.formatMoney(tx.line_profit)
    },

    profitClass(tx) {
      const estimated = this.isAdminMode && tx.cost_is_estimated
      if (estimated) {
        return Number(tx.line_profit) < 0 ? 'text-amber-400' : 'text-amber-300'
      }
      return Number(tx.line_profit) >= 0 ? 'text-emerald-400' : 'text-rose-400'
    },

    renderTrendChart() {
      if (typeof Chart === 'undefined') {
        console.error('Chart.js not loaded! Make sure to include Chart.js v2.9.4')
        return
      }

      const ctx = this.$refs.trendChart
      const groups = (this.detail && this.detail.daily_groups) || []
      if (!ctx || groups.length <= 1) return

      if (this.trendChart) this.trendChart.destroy()

      this.trendChart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: groups.map(g => moment(g.date, 'YYYY-MM-DD').format('jYYYY/jMM/jDD')),
          datasets: [
            {
              label: 'فروش',
              data: groups.map(g => Number(g.revenue || 0)),
              backgroundColor: 'rgba(16, 185, 129, 0.5)',
              borderColor: '#10b981',
              borderWidth: 1,
              type: 'bar',
            },
            {
              label: 'سود',
              data: groups.map(g => Number(g.profit || 0)),
              borderColor: '#f59e0b',
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
              borderWidth: 2,
              fill: false,
              type: 'line',
              pointRadius: 2,
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            labels: { fontColor: '#9ca3af', fontFamily: 'Vazir, sans-serif', fontSize: 10 }
          },
          tooltips: {
            rtl: true,
            mode: 'index',
            intersect: false,
            callbacks: {
              label: (item, data) => {
                const label = data.datasets[item.datasetIndex].label || ''
                return `${label}: ${this.formatMoney(item.yLabel)}`
              }
            }
          },
          scales: {
            xAxes: [{
              ticks: { fontColor: '#9ca3af', fontSize: 9 },
              gridLines: { display: false }
            }],
            yAxes: [{
              ticks: {
                beginAtZero: true,
                fontColor: '#9ca3af',
                fontSize: 9,
                callback: value => this.formatNumber(value)
              },
              gridLines: { color: 'rgba(75, 85, 99, 0.2)' }
            }]
          }
        }
      })
    },
  },

  mounted() {
    this.fetchDetail()
  },

  beforeDestroy() {
    if (this.trendChart) this.trendChart.destroy()
  }
}
</script>

<style scoped>
table {
  border-collapse: collapse;
}

.table-scroll-safe {
  scrollbar-width: thin;
}

.min-w-0 {
  min-width: 0 !important;
}
</style>
